import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "./chart";
import {
  CartesianGrid,
  Cell,
  LabelList,
  PieChart as PieChartLib,
  Pie,
} from "recharts";
import { forwardRef, useMemo } from "react";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const PieChart = forwardRef((props, ref) => {
  const {
    chartData = {},
    chartConfig = {},
    labelKey = "label",
    valueKey = "value",
    title,
    subtitle,
    description,
    hasTooltip = true,
    hasLegend = true,
    hasCartesianGrid = true,
    embedded = false,
    showTotal = true,
    sx = {},
  } = props;

  const totalValue = useMemo(() => {
    return chartData.reduce((acc, curr) => acc + curr[valueKey], 0);
    // eslint-disable-next-line
  }, [valueKey]);

  return (
    <Box
      ref={ref}
      sx={{
        ...(embedded
          ? {
              p: 0,
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            }
          : {
              padding: 2,
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
            }),
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {title && <Typography variant="body1">{title}</Typography>}
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {showTotal && chartConfig[valueKey]?.label != null && (
          <Typography variant="body1">{`${totalValue} ${chartConfig[valueKey].label}`}</Typography>
        )}
      </Box>
      <ChartContainer config={chartConfig}>
        <PieChartLib>
          {hasCartesianGrid && <CartesianGrid vertical={false} />}

          {hasTooltip && (
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  nameKey={labelKey}
                  formatter={(value, name, item) => {
                    const row = item?.payload;
                    const segmentKey = row?.platform || row?.[labelKey] || name;
                    const segmentConfig =
                      chartConfig[segmentKey] || chartConfig[valueKey];
                    const formatted =
                      segmentConfig?.valueFormatter?.(value, row) ??
                      chartConfig[valueKey]?.valueFormatter?.(value, row) ??
                      (value != null ? Number(value).toLocaleString() : "—");
                    const indicatorColor = item?.color || item?.payload?.fill;

                    return (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                          style={{
                            "--color-bg": indicatorColor
                              ? indicatorColor
                              : `var(--color-${segmentKey})`,
                          }}
                        />
                        {segmentConfig?.label ||
                          chartConfig[name]?.label ||
                          row?.[labelKey] ||
                          name}
                        <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                          {formatted}
                        </div>
                      </>
                    );
                  }}
                />
              }
            />
          )}
          {hasLegend && (
            <ChartLegend
              content={<ChartLegendContent nameKey={labelKey} hideLabel />}
            />
          )}
          <Pie data={chartData} dataKey={valueKey} blendStroke="#000">
            {chartData.map((entry, index) => {
              const segmentKey = entry.platform || entry[labelKey];
              return (
                <Cell
                  key={segmentKey ?? index}
                  fill={`var(--color-${segmentKey})`}
                />
              );
            })}
            <LabelList
              dataKey={labelKey}
              className="fill-foreground"
              fontSize={12}
              formatter={(value) => chartConfig[value]?.label}
            />
          </Pie>
        </PieChartLib>
      </ChartContainer>
      {description && (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
});

PieChart.displayName = "PieChart";
export default PieChart;
